/**
 * TipTap-powered rich text editor.
 *
 * Stores HTML; the public blog render layer is expected to render HTML
 * (no markdown pre-processing needed for content authored here).
 *
 * StarterKit gives: bold, italic, strike, code, headings (H1-H6),
 * bullet/ordered lists, list-item, blockquote, code-block, hard-break,
 * horizontal-rule, undo/redo, paragraph, document. We add Link, Image,
 * and Placeholder.
 *
 * Security note: StarterKit does not permit <script>. If we ever need
 * paranoid sanitization, render with `DOMPurify` in the public blog
 * layer — don't try to sanitize on save, since that round-trips poorly
 * with the editor's own <p>/<br> handling.
 */
import { useEditor, EditorContent } from '@tiptap/react'
import { useEffect } from 'react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link as LinkIcon,
  Undo,
  Redo,
  Pilcrow,
} from 'lucide-react'
import { type FC } from 'react'
import { cn } from '../lib/cn'

export interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
  disabled?: boolean
}

const TOOLBAR_BTN =
  'inline-flex items-center justify-center size-8 rounded-md text-slatey hover:bg-sand/60 ' +
  'hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed'

const TOOLBAR_BTN_ACTIVE = 'bg-forest/10 text-forest'

function ToolbarButton({
  isActive,
  onClick,
  disabled,
  title,
  children,
}: {
  isActive?: boolean
  onClick: () => void
  disabled?: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={isActive}
      className={cn(TOOLBAR_BTN, isActive && TOOLBAR_BTN_ACTIVE)}
    >
      {children}
    </button>
  )
}

export const RichTextEditor: FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing…',
  minHeight = 320,
  disabled = false,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class:
          'rte-surface focus:outline-none px-4 py-3 text-ink text-sm leading-relaxed',
        style: `min-height: ${minHeight}px`,
      },
    },
  })

  // Sync external `value` changes into the editor (e.g., when the
  // parent loads a draft post and populates this field). Only syncs
  // when the editor's current HTML actually differs — otherwise we'd
  // overwrite the user's typing on every keystroke.
  //
  // IMPORTANT: don't run this synchronously during render. Doing so
  // can recurse with React 18's concurrent rendering (TipTap's
  // getHTML() always returns '<p></p>' for empty content, while the
  // parent's `value` may be '' — the diff never settles and the
  // component schedules setContent forever). Putting it in an effect
  // makes it a one-shot per value change.
  useEffect(() => {
    if (!editor) return
    if (value === undefined) return
    if (editor.getHTML() === value) return
    editor.commands.setContent(value, false)
  }, [editor, value])

  if (!editor) {
    return (
      <div
        className="rounded-md border border-line bg-white text-sm text-slatey"
        style={{ minHeight }}
      />
    )
  }

  function setLink() {
    const previous = editor!.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL (leave blank to remove)', previous ?? 'https://')
    if (url === null) return
    if (url === '') {
      editor!.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor!.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div className="rounded-md border border-line bg-white focus-within:border-forest focus-within:ring-2 focus-within:ring-forest/20 transition">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line px-2 py-1.5">
        <ToolbarButton
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <Bold className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <Italic className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          isActive={editor.isActive('paragraph') && !editor.isActive('heading')}
          onClick={() => editor.chain().focus().setParagraph().run()}
          title="Paragraph"
        >
          <Pilcrow className="size-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-line" />
        <ToolbarButton
          isActive={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title="Heading 1"
        >
          <Heading1 className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          isActive={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading 2"
        >
          <Heading2 className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          isActive={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Heading 3"
        >
          <Heading3 className="size-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-line" />
        <ToolbarButton
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list"
        >
          <List className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list"
        >
          <ListOrdered className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Blockquote"
        >
          <Quote className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          isActive={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          title="Code block"
        >
          <Code className="size-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-line" />
        <ToolbarButton
          isActive={editor.isActive('link')}
          onClick={setLink}
          title="Link"
        >
          <LinkIcon className="size-4" />
        </ToolbarButton>
        <span className="ml-auto flex items-center gap-0.5">
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <Undo className="size-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <Redo className="size-4" />
          </ToolbarButton>
        </span>
      </div>
      {/* Editor surface */}
      <EditorContent editor={editor} />
    </div>
  )
}
